import React from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, FileSignature, CheckCircle2, AlertTriangle, Ban, ArrowLeft, ArrowRight, Download, ShieldCheck } from "lucide-react";

const STEPS = ["Select Partner", "Agreement Type", "Details", "Preview", "Final Review"];

const errDetail = (e) => e.response?.data?.detail;

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-0 mb-6 overflow-x-auto" data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2 shrink-0">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              i < step ? "bg-emerald-600 text-white" : i === step ? "bg-vs-royal text-white" : "bg-slate-100 text-slate-400"}`}>
              {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
            <span className={`text-xs font-semibold whitespace-nowrap ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`h-px w-6 sm:w-10 mx-2 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function BlockerPanel({ title, items, showProfileLink, nav }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 mb-4" data-testid="wizard-blockers">
      <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm mb-1"><Ban className="h-4 w-4" />{title}</div>
      <ul className="list-disc pl-6 text-xs text-rose-700 space-y-0.5">{items.map((b, i) => <li key={i}>{b}</li>)}</ul>
      {showProfileLink && <Button size="sm" variant="outline" className="mt-3 border-rose-300 text-rose-700"
        onClick={() => nav("/app/company-profile")} data-testid="wizard-open-profile">Open Company Profile</Button>}
    </div>
  );
}

const PARTY_ROWS = [
  ["Legal Name", "legal_name", true], ["CIN", "cin", false], ["PAN", "pan", false], ["GSTIN", "gstin", false],
  ["Registered Address", "address", true], ["Authorized Signatory / Representative", "rep_name", true],
  ["Designation", "rep_designation", true], ["Official Email", "rep_email", true],
];

function PartyCard({ title, party, testid }) {
  return (
    <Card title={title}>
      <div data-testid={testid}>
        {PARTY_ROWS.map(([label, key, mandatory]) => {
          const v = (party?.[key] || "").trim();
          return (
            <div key={key} className="py-1.5 border-b border-slate-50">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
              {v ? <div className="text-sm text-slate-800 break-words">{v}</div>
                : <div className={`text-xs italic ${mandatory ? "text-rose-600 font-semibold not-italic" : "text-slate-400"}`}>
                    {mandatory ? "Missing — blocks finalization" : "Not provided"}</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function AgreementWizard() {
  const nav = useNavigate();
  const [step, setStep] = React.useState(0);
  const [partners, setPartners] = React.useState(null);
  const [partnerId, setPartnerId] = React.useState("");
  const [types, setTypes] = React.useState(null);
  const [typeKey, setTypeKey] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [effective, setEffective] = React.useState("");
  const [expiry, setExpiry] = React.useState("");
  const [detail, setDetail] = React.useState(null);
  const [blockers, setBlockers] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(null);

  React.useEffect(() => {
    api.get("/admin/agreements/eligible-partners").then((r) => setPartners(r.data.items || [])).catch(() => setPartners([]));
  }, []);

  const partner = (partners || []).find((p) => p.id === partnerId);

  const pickPartner = (id) => {
    setPartnerId(id); setTypeKey(""); setTypes(null);
    const p = partners.find((x) => x.id === id);
    if (p) api.get(`/admin/agreements/types?partner_type=${p.type}`).then((r) => setTypes(r.data.items || [])).catch(() => setTypes([]));
  };

  const generate = async () => {
    setBusy(true); setBlockers(null);
    try {
      const r = await api.post("/admin/agreements", {
        agreement_type: typeKey, partner_org_id: partnerId, title: title || undefined,
        effective_date: effective || undefined, expiry_date: expiry || undefined,
      });
      const d = await api.get(`/admin/agreements/${r.data.id}`);
      setDetail(d.data); setStep(3);
      toast.success("Draft generated — review the merged Party A & Party B preview");
    } catch (e) {
      const d = errDetail(e);
      if (d?.code === "COMPANY_PROFILE_INCOMPLETE") setBlockers({ title: d.message, items: d.missing_fields, profile: true });
      else toast.error(apiError(d));
    } finally { setBusy(false); }
  };

  const refreshDetail = async (id) => {
    const d = await api.get(`/admin/agreements/${id}`);
    setDetail(d.data);
  };

  const openPdf = async () => {
    try {
      const r = await api.get(`/admin/agreements/${detail.agreement.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Unable to open PDF preview"); }
  };

  const finalize = async () => {
    setBusy(true); setBlockers(null);
    try {
      const r = await api.post(`/admin/agreements/${detail.agreement.id}/submit`);
      setDone(r.data);
      toast.success("Agreement finalized (READY_TO_SEND). No partner email sent — delivery is Step 4.");
    } catch (e) {
      const d = errDetail(e);
      if (d?.blocking) setBlockers({ title: d.message, items: d.blocking });
      else toast.error(apiError(d));
      await refreshDetail(detail.agreement.id).catch(() => {});
    } finally { setBusy(false); }
  };

  const a = detail?.agreement;
  const val = detail?.validation;

  return (
    <div className="pb-8 max-w-5xl" data-testid="agreement-wizard">
      <PageHeader title="New Agreement" breadcrumb="VS Command Center · Agreements · New"
        description="Party A comes from the System Company Profile; Party B from the selected verified Partner 360° record. Nothing is sent to the partner in this step." />

      {done ? (
        <Card>
          <div className="text-center py-8" data-testid="wizard-success">
            <ShieldCheck className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-display font-bold text-xl text-slate-900">Agreement Finalized</h2>
            <p className="text-sm text-slate-500 mt-2">Status: <strong>READY_TO_SEND</strong> · Final PDF stored securely ({done.final_pdf}).
              Party A &amp; Party B snapshots recorded. Partner dashboard delivery and email happen in Step 4.</p>
            <div className="flex justify-center gap-2 mt-5">
              <Button variant="outline" onClick={() => nav("/app/agreements")} data-testid="wizard-done-list">All Agreements</Button>
              <Button className="bg-vs-royal text-white" onClick={() => nav(`/app/agreements/${a.id}`)} data-testid="wizard-done-open">Open Agreement</Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Stepper step={step} />
          {blockers && <BlockerPanel title={blockers.title} items={blockers.items} showProfileLink={blockers.profile} nav={nav} />}

          {step === 0 && (
            <Card title="Select a verified partner" subtitle="Only verified / approved / active partners are eligible. Party B is pulled automatically from the Partner 360° record.">
              {partners === null ? <SkeletonRows rows={4} /> : partners.length === 0 ? (
                <p className="text-sm text-slate-400">No eligible partners. Verify a partner in Partner 360° first.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="wizard-partners">
                  {partners.map((p) => (
                    <button key={p.id} onClick={() => pickPartner(p.id)} data-testid={`wizard-partner-${p.id}`}
                      className={`text-left rounded-xl border p-4 transition-all ${partnerId === p.id ? "border-vs-royal bg-vs-light ring-1 ring-vs-royal" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-white border border-slate-100 flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-vs-royal" /></div>
                        <div className="min-w-0"><div className="font-semibold text-sm text-slate-800 truncate">{p.name}</div>
                          <div className="text-xs text-slate-400 capitalize">{p.type} · {p.partner_code}</div></div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-end pt-5">
                <Button disabled={!partnerId} onClick={() => setStep(1)} className="bg-vs-royal text-white" data-testid="wizard-next-1">Continue<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </Card>
          )}

          {step === 1 && (
            <Card title={`Agreement type for ${partner?.name || ""}`} subtitle="Types available for this partner type.">
              {types === null ? <SkeletonRows rows={4} /> : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="wizard-types">
                  {types.map((t) => (
                    <button key={t.key} onClick={() => setTypeKey(t.key)} data-testid={`wizard-type-${t.key}`}
                      className={`text-left rounded-xl border p-4 transition-all ${typeKey === t.key ? "border-vs-royal bg-vs-light ring-1 ring-vs-royal" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-start gap-3">
                        <FileSignature className="h-4 w-4 text-vs-royal mt-0.5 shrink-0" />
                        <div><div className="font-semibold text-sm text-slate-800">{t.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{t.desc}</div></div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-between pt-5">
                <Button variant="outline" onClick={() => setStep(0)} data-testid="wizard-back-0"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                <Button disabled={!typeKey} onClick={() => setStep(2)} className="bg-vs-royal text-white" data-testid="wizard-next-2">Continue<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </Card>
          )}

          {step === 2 && (
            <Card title="Agreement details" subtitle="A draft document is generated immediately. You will preview the merged parties before finalizing.">
              <div className="space-y-4">
                <div><Label>Title (optional)</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" placeholder="Defaults to agreement type + partner name" data-testid="wizard-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Effective Date</Label><Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mt-1" data-testid="wizard-effective" /></div>
                  <div><Label>Expiry Date</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" data-testid="wizard-expiry" /></div>
                </div>
              </div>
              <div className="flex justify-between pt-5">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-1"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                <Button disabled={busy} onClick={generate} className="bg-vs-royal text-white" data-testid="wizard-generate">
                  {busy ? "Generating…" : "Generate Draft"}<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </Card>
          )}

          {step === 3 && a && (
            <>
              <div className="mb-4 rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-500 flex items-center justify-between gap-3 flex-wrap">
                <span><strong className="text-slate-700">{a.agreement_number}</strong> · v{a.version} · This preview shows the exact merged Party A &amp; Party B that will appear in the PDF.</span>
                <Button size="sm" variant="outline" onClick={openPdf} data-testid="wizard-pdf-preview"><Download className="h-3.5 w-3.5 mr-1" />Open PDF Preview</Button>
              </div>
              <div className="grid lg:grid-cols-2 gap-5 mb-5">
                <PartyCard title="Party A — VS-Solutions (Company Profile)" party={a.party_a} testid="preview-party-a" />
                <PartyCard title={`Party B — ${a.partner_name} (Partner 360°)`} party={a.party_b} testid="preview-party-b" />
              </div>
              <Card title="Document outline" subtitle={`${(a.document?.sections || []).length} sections · ${(a.document?.schedules || []).length} schedules · ${(a.document?.annexures || []).length} annexures`}>
                <ol className="list-decimal pl-5 text-sm text-slate-600 space-y-1 columns-1 sm:columns-2" data-testid="wizard-outline">
                  {(a.document?.sections || []).map((s, i) => <li key={i}>{s.title}</li>)}
                </ol>
              </Card>
              <div className="flex justify-between pt-5">
                <Button variant="outline" onClick={() => nav(`/app/agreements/${a.id}`)} data-testid="wizard-edit-draft">Edit Draft</Button>
                <Button onClick={async () => { await refreshDetail(a.id); setStep(4); }} className="bg-vs-royal text-white" data-testid="wizard-next-4">Proceed to Final Review<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </>
          )}

          {step === 4 && a && (
            <Card title="Final Review" subtitle="All blocking checks must pass. Warnings are configuration items that stay marked in the document and do not block finalization.">
              <div className="space-y-2" data-testid="wizard-final-checks">
                {(val?.checks || []).map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      : c.severity === "blocking" ? <Ban className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                      : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />}
                    <div><span className={c.ok ? "text-slate-700" : c.severity === "blocking" ? "text-rose-700 font-medium" : "text-amber-700"}>{c.check}</span>
                      {c.detail ? <div className="text-xs text-slate-400">{c.detail}</div> : null}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mt-4 text-xs text-slate-500">
                Finalizing stores the PDF securely, snapshots Party A &amp; Party B, links the agreement to {a.partner_name} and sets status <strong>READY_TO_SEND</strong>. No partner email or notification is sent in this step.
              </div>
              <div className="flex justify-between pt-5">
                <Button variant="outline" onClick={() => setStep(3)} data-testid="wizard-back-3"><ArrowLeft className="h-4 w-4 mr-1" />Back to Preview</Button>
                <Button disabled={busy || (val?.blocking || []).length > 0} onClick={finalize} className="bg-emerald-600 text-white" data-testid="wizard-finalize">
                  {busy ? "Finalizing…" : (val?.blocking || []).length > 0 ? `${val.blocking.length} Blocking Issue(s)` : "Finalize Agreement"}</Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
